//dependencies
import { formatISO } from "date-fns";

//service contracts
import {
	EmployeeCustomFieldUpdate,
	EmployeeCustomFieldUpdateJSON,
	GetEmployeeDetailResponse,
	GetEmployeeDetailsHistoryResponse,
	GetEmployeeDetailsResponseJSON,
} from "@performyard/service-contracts";

import { queryClient } from "../../../queryClient";
import { MeResponse } from "../auth";
import request, { ErrorResponse } from "./internal-base-request";

export const getEmployeeCustomFields = async (employeeId: string): Promise<GetEmployeeDetailsResponseJSON> => {
	const [response] = await request<GetEmployeeDetailsResponseJSON>(`/v1/employee-details/${employeeId}`);
	return response;
};

export const getEmployeeCustomField = async (
	employeeId: string,
	fieldId: string
): Promise<GetEmployeeDetailResponse> => {
	const [response] = await request<GetEmployeeDetailResponse>(`/v1/employee-details/${employeeId}/${fieldId}`);
	return response;
};

export const getEmployeeCustomFieldHistory = async (
	employeeId: string,
	fieldId: string
): Promise<GetEmployeeDetailsHistoryResponse> => {
	const [response] = await request<GetEmployeeDetailsHistoryResponse>(
		`/v1/employee-details/history/${employeeId}/${fieldId}`
	);
	return response;
};

export const patchEmployeeCustomFields = async (
	updates: EmployeeCustomFieldUpdate[],
	employeeId: string,
	asOf: Date
) => {
	const [response] = await request(`/v1/employee-details/${employeeId}`, {
		method: "PATCH",
		body: JSON.stringify({ updates, asOf: formatISO(asOf) }),
	});
	return response;
};

export const patchEmployeeBulkCustomFields = async (
	updatesList: {
		updates: EmployeeCustomFieldUpdateJSON[];
		employeeId: string;
		asOf: Date;
	}[]
) => {
	const [response] = await request(`/v1/employee-details/`, {
		method: "PATCH",
		body: JSON.stringify(updatesList),
	});
	return response;
};

export const deleteEmployeeCustomFieldHistory = async (employeeId: string, historyId: string): Promise<any> => {
	const [response] = await request(`/v1/employee-details/${employeeId}/${historyId}`, {
		method: "DELETE",
	});
	return response;
};

export const editEmployeeActivation = async (
	employeeId: string,
	activation: boolean,
	terminationDate?: Date,
	managerReassignmentId?: string
) => {
	const [response] = await request(`/v1/employee-details/activation/${employeeId}`, {
		method: "PATCH",
		body: JSON.stringify({ activation, terminationDate: terminationDate?.toISOString(), managerReassignmentId }),
	});
	return response;
};

export const postEmployeeAvatar = async (employeeID: string, file: File) => {
	const formData = new FormData();
	formData.append("image", file);
	try {
		const currentUser = queryClient.getQueryData(["auth-current-user"]) as MeResponse;

		let url = `/v1/employee-details/${employeeID}/avatar`;
		// If I'm localhost than just use localhost with post 3000
		if (window.location.hostname === "localhost") {
			url = "http://localhost:3030" + url;
		} else {
			url = "https://internal-api." + window.location.hostname + url;
		}

		const response = await fetch(url, {
			method: "POST",
			headers: {
				"X-CSRFToken": window.xsrf_val,
				Authorization: `Bearer ${currentUser.me.authorization?.token}`,
			},
			body: formData,
		});

		const data = await response.json();

		if (!response.ok) {
			throw new ErrorResponse(data?.error || "Bad fetch response");
		}

		return response;
	} catch (error) {
		throw error;
	}
};

export const deleteEmployeeAvatar = async (employeeId: string) => {
	const [response] = await request(`/v1/employee-details/${employeeId}/avatar`, {
		method: "DELETE",
	});
	return response;
};
